import { useEffect, useRef } from "react";
import { apiUrl } from "@/api/client";

export interface Tick {
  time_msc: number;
  bid: number;
  ask: number;
}

export interface TickStreamHandlers {
  /** Called once on connect with the most recent buffered ticks. */
  onSnapshot?: (ticks: Tick[]) => void;
  /** Called for every live batch the server pushes after connect. */
  onTicks: (ticks: Tick[]) => void;
}

/**
 * Subscribe to /api/stream/ticks for a given symbol. The EventSource
 * is re-created on symbol change, never on handler change — handlers
 * are held in a ref so renderers can pass fresh closures without
 * tearing down the connection.
 */
export function useTickStream(
  symbol: string,
  handlers: TickStreamHandlers,
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!symbol) return;
    const url = apiUrl(
      `/api/stream/ticks?symbol=${encodeURIComponent(symbol)}`,
    );
    // withCredentials so the session cookie rides on the cross-origin SSE
    // (no-op when same-origin).
    const es = new EventSource(url, { withCredentials: true });

    const handleSnapshot = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        handlersRef.current.onSnapshot?.(data.ticks ?? []);
      } catch {
        // ignore malformed
      }
    };
    const handleTick = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        handlersRef.current.onTicks(data.ticks ?? []);
      } catch {
        // ignore malformed
      }
    };
    es.addEventListener("snapshot", handleSnapshot as EventListener);
    es.addEventListener("tick", handleTick as EventListener);

    return () => {
      es.removeEventListener("snapshot", handleSnapshot as EventListener);
      es.removeEventListener("tick", handleTick as EventListener);
      es.close();
    };
  }, [symbol]);
}
